import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import type { ImageBoardItem } from "../../app/services/imageBoard";
import BoardStrokeLayer, { type DrawableStroke } from "./BoardStrokeLayer";

/** 中身の外側に空ける余白（ワールド単位） */
const PADDING = 24;
const NO_ERASE: ReadonlySet<string> = new Set();

const Frame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background-color: #fcfcfc;
`;

const World = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  transform-origin: 0 0;

  img {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    object-fit: fill;
    user-select: none;
    -webkit-user-drag: none;
  }
`;

interface BoardSnapshotProps {
  items: Pick<ImageBoardItem, "id" | "image_url" | "x" | "y" | "width" | "height" | "z_index">[];
  strokes: DrawableStroke[];
}

/**
 * イメージボードの閲覧専用スナップショット（共有リンクの閲覧ページ用）。
 * 画像と手描きの全体が収まるように、表示幅に合わせて縮小して並べる
 * （拡大はしない）。パン / ズーム / 編集はできない。
 */
const BoardSnapshot: React.FC<BoardSnapshotProps> = ({ items, strokes }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState(0);

  const bounds = useMemo(() => {
    const xs: number[] = [];
    const ys: number[] = [];
    items.forEach((item) => {
      xs.push(item.x, item.x + item.width);
      ys.push(item.y, item.y + item.height);
    });
    strokes.forEach((stroke) => {
      stroke.points.forEach(([x, y]) => {
        xs.push(x - stroke.width, x + stroke.width);
        ys.push(y - stroke.width, y + stroke.width);
      });
    });
    if (xs.length === 0) return null;
    const minX = Math.min(...xs) - PADDING;
    const minY = Math.min(...ys) - PADDING;
    return {
      minX,
      minY,
      width: Math.max(...xs) + PADDING - minX,
      height: Math.max(...ys) + PADDING - minY,
    };
  }, [items, strokes]);

  const hasContent = bounds !== null;
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    setFrameWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setFrameWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasContent]);

  if (!bounds) {
    return (
      <p className="bodytext-13 u-text-light-gray">まだ画像がありません</p>
    );
  }

  const scale = frameWidth > 0 ? Math.min(1, frameWidth / bounds.width) : 0;
  const ordered = [...items].sort((a, b) => a.z_index - b.z_index);

  return (
    <Frame ref={frameRef} style={{ height: bounds.height * scale }}>
      <World
        style={{
          transform: `scale(${scale}) translate(${-bounds.minX}px, ${-bounds.minY}px)`,
        }}
      >
        {ordered.map((item) => (
          <img
            key={item.id}
            src={item.image_url}
            alt=""
            loading="lazy"
            draggable={false}
            style={{
              transform: `translate(${item.x}px, ${item.y}px)`,
              width: item.width,
              height: item.height,
              zIndex: item.z_index,
            }}
          />
        ))}
        <BoardStrokeLayer
          strokes={strokes}
          livePoints={null}
          liveColor=""
          liveWidth={0}
          pendingEraseIds={NO_ERASE}
        />
      </World>
    </Frame>
  );
};

export default BoardSnapshot;
